import React, { useState } from 'react';
import { MessageSquareIcon, PhoneIcon, BellIcon, MailIcon } from 'lucide-react';
interface CommunicationHistoryProps {
  patientId: number;
}
const mockCommunications = [{
  id: 1,
  type: 'message',
  date: '2024-01-15 10:30 AM',
  subject: 'Appointment Confirmation',
  content: 'Your appointment with Dr. Sarah Johnson is confirmed for Jan 20, 2024 at 10:00 AM',
  status: 'Sent'
}, {
  id: 2,
  type: 'call',
  date: '2024-01-12 2:15 PM',
  subject: 'Follow-up Call',
  content: 'Called to check on medication side effects. Patient reported no issues.',
  status: 'Completed'
}, {
  id: 3,
  type: 'email',
  date: '2024-01-10 9:00 AM',
  subject: 'Lab Results Available',
  content: 'Your recent lab results are now available in your patient portal.',
  status: 'Sent'
}, {
  id: 4,
  type: 'reminder',
  date: '2024-01-08 8:00 AM',
  subject: 'Appointment Reminder',
  content: 'Reminder: You have an appointment tomorrow at 10:00 AM',
  status: 'Sent'
}, {
  id: 5,
  type: 'message',
  date: '2024-01-05 3:45 PM',
  subject: 'Prescription Refill',
  content: 'Your prescription for Lisinopril has been refilled and is ready for pickup.',
  status: 'Sent'
}];
export function CommunicationHistory({
  patientId
}: CommunicationHistoryProps) {
  const [filterType, setFilterType] = useState<'all' | 'message' | 'call' | 'email' | 'reminder'>('all');
  const filteredCommunications = filterType === 'all' ? mockCommunications : mockCommunications.filter(comm => comm.type === filterType);
  const getIcon = (type: string) => {
    switch (type) {
      case 'message':
        return <MessageSquareIcon className="w-5 h-5 text-blue-600" data-id="element-675" />;
      case 'call':
        return <PhoneIcon className="w-5 h-5 text-green-600" data-id="element-676" />;
      case 'email':
        return <MailIcon className="w-5 h-5 text-purple-600" data-id="element-677" />;
      case 'reminder':
        return <BellIcon className="w-5 h-5 text-orange-600" data-id="element-678" />;
      default:
        return <MessageSquareIcon className="w-5 h-5 text-gray-600" data-id="element-679" />;
    }
  };
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'message':
        return 'bg-blue-100 text-blue-800';
      case 'call':
        return 'bg-green-100 text-green-800';
      case 'email':
        return 'bg-purple-100 text-purple-800';
      case 'reminder':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="space-y-6" data-id="element-680">
      {/* Header with New Communication Button */}
      <div className="flex items-center justify-between" data-id="element-681">
        <h2 className="text-xl font-bold text-gray-900" data-id="element-682">
          Communication History
        </h2>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-683">
          New Communication
        </button>
      </div>
      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-2" data-id="element-684">
        <button onClick={() => setFilterType('all')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterType === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-685">
          All Communications
        </button>
        <button onClick={() => setFilterType('message')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterType === 'message' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-686">
          Messages
        </button>
        <button onClick={() => setFilterType('call')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterType === 'call' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-687">
          Calls
        </button>
        <button onClick={() => setFilterType('email')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterType === 'email' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-688">
          Emails
        </button>
        <button onClick={() => setFilterType('reminder')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterType === 'reminder' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-689">
          Reminders
        </button>
      </div>
      {/* Communication List */}
      <div className="space-y-4" data-id="element-690">
        {filteredCommunications.map(comm => <div key={comm.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-id="element-691">
            <div className="flex items-start space-x-4" data-id="element-692">
              <div className="flex-shrink-0" data-id="element-693">{getIcon(comm.type)}</div>
              <div className="flex-1 min-w-0" data-id="element-694">
                <div className="flex items-center justify-between mb-2" data-id="element-695">
                  <h4 className="font-semibold text-gray-900" data-id="element-696">
                    {comm.subject}
                  </h4>
                  <div className="flex items-center space-x-2" data-id="element-697">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(comm.type)}`} data-id="element-698">
                      {comm.type.charAt(0).toUpperCase() + comm.type.slice(1)}
                    </span>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800" data-id="element-699">
                      {comm.status}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-700 mb-2" data-id="element-700">{comm.content}</p>
                <p className="text-xs text-gray-500" data-id="element-701">{comm.date}</p>
              </div>
            </div>
          </div>)}
      </div>
    </div>;
}